import { Response } from "miragejs"

// Keep a registry of routes and origins added dynamically by child apps so the parent owns the final Mirage config.
window.__offlineDynamicMockRegistry = window.__offlineDynamicMockRegistry || {
    pending: [],
    origins: [],
};

const normalizeOrigin = (origin) => {
    if (!origin || typeof origin !== 'string') return null;

    const trimmed = origin.trim();
    if (!trimmed) return null;

    try {
        const parsed = new URL(trimmed);
        const normalizedOrigin = parsed.origin.endsWith('/') ? parsed.origin : `${parsed.origin}/`;
        return normalizedOrigin;
    } catch (_error) {
        return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
    }
};

// Convert a child app route definition into a Mirage-compatible handler.
const normalizeChildAppRoute = (routeDef) => {
    if (!routeDef || typeof routeDef !== 'object') return null;

    const method = (routeDef.method || routeDef.type || 'get').toLowerCase();
    const url = routeDef.url || routeDef.path;

    if (!url) return null;

    const routeHandler = (_schema, req) => {
        const response = typeof routeDef.response === 'function'
            ? routeDef.response({ req, schema: _schema, appId: routeDef.appId })
            : routeDef.response;

        if (response instanceof Response) return response;
        if (typeof response === 'undefined') {
            return new Response(routeDef.status || 200, routeDef.headers || {}, routeDef.body || {});
        }

        return new Response(routeDef.status || 200, routeDef.headers || {}, response);
    };

    return { method, url, routeHandler };
};

const registerOfflineAppOrigins = ({ appId, origins } = {}) => {
    const registry = window.__offlineDynamicMockRegistry || (window.__offlineDynamicMockRegistry = { pending: [], origins: [] });
    const entries = Array.isArray(origins)
        ? origins
        : (typeof origins === 'string' ? [origins] : []);

    const normalizedOrigins = entries
        .map(normalizeOrigin)
        .filter(Boolean);

    if (!normalizedOrigins.length) return registry.origins || [];

    registry.origins = Array.from(new Set([...(registry.origins || []), ...normalizedOrigins]));

    if (appId) {
        registry.appOrigins = registry.appOrigins || {};
        registry.appOrigins[appId] = Array.from(new Set([...(registry.appOrigins[appId] || []), ...normalizedOrigins]));
    }

    return registry.origins;
};

const unregisterOfflineAppOrigins = ({ appId } = {}) => {
    const registry = window.__offlineDynamicMockRegistry || (window.__offlineDynamicMockRegistry = { pending: [], origins: [] });
    if (!appId) return registry.origins || [];

    const appOrigins = registry.appOrigins?.[appId] || [];
    if (appOrigins.length) {
        registry.origins = (registry.origins || []).filter((origin) => !appOrigins.includes(origin));
    }

    delete registry.appOrigins?.[appId];
    return registry.origins || [];
};

// Register additional routes supplied by a child app. This is the hook used by shadow-DOM apps.
const registerOfflineAppMocks = ({ appId, routes, origins, server = window.offlineServerInstance } = {}) => {
    if (origins) {
        registerOfflineAppOrigins({ appId, origins });
    }

    if (!server) {
        const registry = window.__offlineDynamicMockRegistry || (window.__offlineDynamicMockRegistry = { pending: [], origins: [] });
        const pending = registry.pending || (registry.pending = []);
        pending.push({ appId, routes, origins });
        return pending;
    }

    const registry = window.__offlineDynamicMockRegistry || (window.__offlineDynamicMockRegistry = { pending: [], origins: [] });
    // Support either a list of route objects or a keyed map of route objects.
    const normalizedRoutes = Array.isArray(routes)
        ? routes
        : Object.values(routes || {});

    const registeredRoutes = [];

    normalizedRoutes.forEach((routeDef) => {
        const normalizedRoute = normalizeChildAppRoute({ ...routeDef, appId });

        if (!normalizedRoute) return;

        // Attach the route to the current Mirage server so the child app can call its own APIs while mocked.
        server[normalizedRoute.method](normalizedRoute.url, normalizedRoute.routeHandler);
        registeredRoutes.push({ appId, ...normalizedRoute });
    });

    if (appId) {
        registry[appId] = registeredRoutes;
    }

    return registeredRoutes;
};

const unregisterOfflineAppMocks = ({ appId, server = window.offlineServerInstance } = {}) => {
    if (!appId) {
        return { appId: null, removed: false };
    }

    const registry = window.__offlineDynamicMockRegistry || (window.__offlineDynamicMockRegistry = { pending: [], origins: [] });
    const appRoutes = registry[appId] || [];

    delete registry[appId];
    unregisterOfflineAppOrigins({ appId });

    if (server && appRoutes.length) {
        appRoutes.forEach((routeDef) => {
            try {
                server[routeDef.method](routeDef.url, () => new Response(404, {}, { error: 'Mock route removed for unmounted app' }));
            } catch (_error) {
                // Mirage does not expose a public route-removal API; the registry cleanup is the source of truth.
            }
        });
    }

    return { appId, removed: true };
};

export { registerOfflineAppMocks, unregisterOfflineAppMocks }
